<template>
  <base-slide-over ref="filterSlideover" title="Filter Activities" size="lg">
    <base-form ref="form" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" @submit.prevent>
      <div class="tw-text-normal tw-font-normal tw-mb-2.5">Due Date</div>
      <base-button-toggle-group ref="dateToggle" size="xs" :options="dateShortcuts" @selected="dateShortcutSelected" class="tw-mb-2.5" />
      <div v-show="showDateFields" class="tw-flex tw-mt-2.5 tw-gap-4">
        <base-date-picker label="Start Date" v-model="startDate" :typeable="true" :inline="false" />
        <base-date-picker label="End Date" v-model="endDate" :typeable="true" :inline="false" />
      </div>
      <div class="tw-text-normal tw-font-normal tw-mb-2.5">Locations</div>
      <base-advanced-select placeholder="Select location(s)" :options="orderedLocations" label-key="name" value-key="businessLocationId" multiple v-model="locationsFilter" />
      <div class="tw-text-normal tw-font-normal tw-mt-5 tw-mb-2.5">Activity Types</div>
      <base-advanced-select placeholder="Select activity type(s)" :options="itemTypes" label-key="label" multiple v-model="typesFilter" />
      <div class="tw-text-normal tw-font-normal tw-mt-5 tw-mb-2.5">Assigned To</div>
      <base-advanced-select placeholder="Select assigned to" :options="businessUsers" label-key="fullName" value-key="userId" multiple v-model="assignedToFilter" />
    </base-form>
    <template #fixed-bottom>
      <div class="tw-p-3 tw-flex tw-justify-between">
        <base-button type="flat" color="red" class="tw-w-auto tw-mr-2" @click="clearFilters">
          Clear All
        </base-button>
        <div>
          <base-button type="flat" class="tw-w-auto tw-mr-2" @click="$refs.filterSlideover.hide()">
            Cancel
          </base-button>
          <base-button class="tw-w-auto" @click="applyFilter">
            Apply
          </base-button>
        </div>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "FilterSlideover",
  props: {
    businessId: { type: String },
  },
  components: {},
  data() {
    return {
      itemTypes: [],
      locationsFilter: [],
      typesFilter: [],
      businessUsers: [],
      assignedToFilter: [],
      startDate: null,
      endDate: null,
      showDateFields: false,
      dateShortcuts: [
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "This Week", value: "week" },
        { label: "This month", value: "month" },
        { label: "Custom", value: "custom" },
      ],
    };
  },
  computed: {
    ...mapGetters("location", ["locations"]),
    orderedLocations() {
      return this.locations.slice().sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
    },
  },
  created() {
    this.loadTypes();
    this.loadBusinessUsers();
  },
  methods: {
    show() {
      this.$refs.filterSlideover.show();
    },
    hide() {
      this.$refs.filterSlideover.hide();
    },
    clearFilters() {
      this.reset();
      this.applyFilter();
    },
    reset() {
      this.locationsFilter = [];
      this.typesFilter = [];
      this.assignedToFilter = [];
      this.startDate = null;
      this.endDate = null;
      this.showDateFields = false;
    },
    loadTypes() {
      let url = `/businesses/${this.businessId}/todo/types`;

      this.$axios.get(url).then((res) => {
        this.itemTypes = res.data.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0));
      });
    },
    applyFilter() {
      if (this.startDate) this.startDate.setHours(0, 0, 0, 0);
      if (this.endDate) this.endDate.setHours(23, 59, 59, 999);

      this.$emit("filter-updated", {
        locationsFilter: this.locationsFilter.map((x) => x.businessLocationId),
        typesFilter: this.typesFilter.map((x) => x.todoItemTypeId),
        assignedToFilter: this.assignedToFilter.map((x) => x.userId),
        startDate: this.startDate,
        endDate: this.endDate,
      });

      this.hide();
    },
    loadBusinessUsers() {
      this.$axios.get(`/businesses/${this.businessId}/users/all`).then((res) => {
        this.businessUsers = res.data.sort((a, b) => (a.fullName > b.fullName ? 1 : b.fullName > a.fullName ? -1 : 0));
        var index = this.businessUsers.findIndex((x) => x.userId === this.userId);

        if (index > 0) this.businessUsers[index].fullName = "(Me) " + this.businessUsers[index].fullName;
      });
    },
    dateShortcutSelected(value) {
      switch (value) {
        case "today":
          this.startDate = new Date();
          this.startDate.setHours(0, 0, 0, 0);
          this.endDate = new Date();
          this.showDateFields = false;
          break;
        case "yesterday":
          this.startDate = new Date();
          this.startDate.setDate(this.startDate.getDate() - 1);
          this.startDate.setHours(0, 0, 0, 0);
          this.endDate = new Date();
          this.endDate.setDate(this.endDate.getDate() - 1);
          this.endDate.setHours(23, 59, 59, 999);
          this.showDateFields = false;
          break;
        case "week":
          this.startDate = new Date();
          this.startDate.setDate(this.startDate.getDate() - this.startDate.getDay());
          this.startDate.setHours(0, 0, 0, 0);
          this.endDate = new Date();
          this.endDate.setDate(this.startDate.getDate() + 7);
          this.endDate.setHours(23, 59, 59, 999);
          this.showDateFields = false;
          break;
        case "month":
          this.startDate = new Date();
          this.startDate.setDate(1);
          this.startDate.setHours(0, 0, 0, 0);
          this.endDate = new Date();
          var month = this.endDate.getMonth();
          var year = this.endDate.getFullYear();
          this.endDate = new Date(year, month + 1, 0);
          this.endDate.setHours(23, 59, 59, 999);
          this.showDateFields = false;
          break;
        case "custom":
          this.startDate = null;
          this.endDate = null;
          this.showDateFields = true;
          break;
      }
    },
  },
};
</script>
